export const MAX_VIDEO_SHOTS = 6;
const PHOTO_NAME = /^(?=.{1,128}$)[A-Za-z0-9][A-Za-z0-9._-]*\.(?:jpe?g|png|webp)$/i;

/** @typedef {{photo:string, motion:"auto"|"photo_motion"}} VideoShot */
/** @typedef {{photo:string, url:string, label?:string, available?:boolean,
 * assessment?:"ready"|"not_assessed"|"not_suitable", reason?:string}} VideoPhoto */
/** modeChosen is set once the owner picks a mode themselves; until then the
 * mode follows defaultVideoMode as ai_enabled arrives. It never reaches a request.
 * @typedef {{renderMode:"ai"|"photo_motion"|"smart_mix", aspectRatio:"16:9"|"9:16",
 * shotPlan:VideoShot[], modeChosen?:boolean}} VideoShotSelection */

/** @param {"ai"|"photo_motion"|"smart_mix"} [mode] @returns {VideoShotSelection} */
export const emptyVideoSelection = (mode = "photo_motion") => ({
  renderMode: mode,
  aspectRatio: "16:9",
  shotPlan: [],
});

/** The mode an untouched editor starts in. The 09-25 release defaulted every
 * editor to photo motion, so an owner who never opened the menu got zooms only
 * (Sam, 2026-09-30). AI is the default again wherever the server offers it: the
 * report page's "Choose for me" (legacy automatic AI) where automatic selection
 * is allowed, AI motion where suitable (smart mix) otherwise.
 * @param {{aiEnabled?:boolean, allowAutomatic?:boolean}} options
 * @returns {"ai"|"photo_motion"|"smart_mix"} */
export const defaultVideoMode = ({ aiEnabled = false, allowAutomatic = false } = {}) =>
  !aiEnabled ? "photo_motion" : allowAutomatic ? "ai" : "smart_mix";

/** The same photos, order, and format under another mode. Every row follows the
 * mode, so a fallback to photo motion never leaves an "auto" row behind.
 * @param {VideoShotSelection} selection @param {"ai"|"photo_motion"|"smart_mix"} mode
 * @returns {VideoShotSelection} */
export const withVideoMode = (selection, mode) => ({
  ...selection,
  renderMode: mode,
  shotPlan: selection.shotPlan.map((shot) => ({
    ...shot,
    motion: mode === "smart_mix" ? "auto" : "photo_motion",
  })),
});

/** Ordered selections are commands, not a sortable set of preferences.
 * @param {unknown} mode @param {unknown} value @returns {VideoShot[]} */
export const normalizeVideoShotPlan = (mode, value) => {
  if (
    !["photo_motion", "smart_mix"].includes(/** @type {string} */ (mode)) ||
    !Array.isArray(value) ||
    !value.length ||
    value.length > MAX_VIDEO_SHOTS
  )
    throw new Error("Choose between 1 and 6 photos for this video.");
  const names = new Set();
  return value.map((shot) => {
    if (
      !shot ||
      typeof shot !== "object" ||
      Array.isArray(shot) ||
      Object.keys(shot).length !== 2 ||
      typeof shot.photo !== "string" ||
      !PHOTO_NAME.test(shot.photo) ||
      names.has(shot.photo) ||
      !["auto", "photo_motion"].includes(shot.motion) ||
      (mode === "photo_motion" && shot.motion !== "photo_motion")
    )
      throw new Error("The video photo selection is invalid. Choose the photos again.");
    names.add(shot.photo);
    return { photo: shot.photo, motion: shot.motion };
  });
};

/** @param {VideoShotSelection} selection */
export const videoPlanOptions = (selection) => {
  if (!["16:9", "9:16"].includes(selection.aspectRatio)) throw new Error("Choose a video format.");
  const plan = normalizeVideoShotPlan(selection.renderMode, selection.shotPlan);
  return {
    render_mode: selection.renderMode,
    shot_plan: plan,
    aspect_ratio: selection.aspectRatio,
    requested_clip_count: plan.length,
  };
};

/** Use the current document's original source identities, never enhanced photos.
 * Missing stored AI analysis is unknown, not proof a photo is unusable.
 * @param {Record<string,any>|null|undefined} doc @returns {VideoPhoto[]} */
export const videoPhotosFromDocument = (doc) => {
  const manifest = doc?.photo_manifest;
  if (!manifest || typeof manifest !== "object" || Array.isArray(manifest)) return [];
  const assessments = new Map();
  for (const section of Array.isArray(doc?.content?.sections) ? doc.content.sections : []) {
    if (section?.__type__ !== "PhotoSequenceSection") continue;
    for (const candidate of Array.isArray(section.strategy?.video_candidates)
      ? section.strategy.video_candidates
      : []) {
      if (typeof candidate?.hero_image !== "string") continue;
      assessments.set(candidate.hero_image, {
        assessment:
          candidate.eligible === true
            ? "ready"
            : candidate.eligible === false
              ? "not_suitable"
              : "not_assessed",
      });
    }
  }
  return Object.entries(manifest)
    .filter(([photo, url]) => PHOTO_NAME.test(photo) && typeof url === "string" && !!url)
    .map(([photo, url]) => ({
      photo,
      url,
      label: typeof doc?.photo_labels?.[photo] === "string" ? doc.photo_labels[photo] : photo,
      available: true,
      assessment: "not_assessed",
      ...assessments.get(photo),
    }));
};

/** @param {VideoShotSelection} selection @param {VideoPhoto[]} photos */
export const videoSelectionIsAvailable = (selection, photos) => {
  try {
    const names = new Set(
      photos.filter((photo) => photo.available !== false).map((photo) => photo.photo)
    );
    return normalizeVideoShotPlan(selection.renderMode, selection.shotPlan).every((shot) =>
      names.has(shot.photo)
    );
  } catch {
    return false;
  }
};
